import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CompactChannelSelector } from "@/components/telemetry/CompactChannelSelector";
import type { ChannelColorIndices } from "@/components/telemetry/channelColors";
import type { ChannelMetadata } from "@/types";

interface ChannelsPanelProps {
  isLoading: boolean;
  error: unknown;
  telemetryMissing: boolean;
  hasTelemetryData: boolean;
  channels: Record<string, ChannelMetadata>;
  selectedChannels: string[];
  channelColorIndices: ChannelColorIndices;
  onSelectionChange: (channels: string[]) => void;
}

export default function ChannelsPanel({
  isLoading,
  error,
  telemetryMissing,
  hasTelemetryData,
  channels,
  selectedChannels,
  channelColorIndices,
  onSelectionChange,
}: ChannelsPanelProps) {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <div className="flex-1 bg-[#0d0d14] border-b border-[#1a1a2e] overflow-hidden flex flex-col">
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <div className="h-5 w-5 border-2 border-[#a855f7] border-t-transparent rounded-full mx-auto mb-2 animate-spin" />
            <p className="text-sm text-[#4b5563]">{t("pages:sessions.analytics.loadingChannels")}</p>
          </div>
        </div>
      ) : error && !telemetryMissing ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="h-8 w-8 text-[#f87171] mx-auto mb-2 opacity-60" />
            <p className="text-sm font-medium text-[#f87171]">{t("pages:sessions.analytics.failedToLoadTelemetry")}</p>
            <p className="text-xs text-[#374151] mt-1">{t("pages:sessions.analytics.tryRefreshOrCheckConnection")}</p>
          </div>
        </div>
      ) : !hasTelemetryData ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="h-8 w-8 text-[#374151] mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium text-[#4b5563]">{t("pages:sessions.analytics.noTelemetryData")}</p>
            <p className="text-xs text-[#374151] mt-1">{t("pages:sessions.analytics.uploadTelemetryToAnalyze")}</p>
          </div>
        </div>
      ) : (
        <CompactChannelSelector
          channels={channels}
          selectedChannels={selectedChannels}
          channelColorIndices={channelColorIndices}
          onSelectionChange={onSelectionChange}
          maxSelections={8}
          className="w-full h-full"
        />
      )}
    </div>
  );
}
